import { ref } from "vue";
import { ApolloQueryResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { localStorageGetCustomerCode } from "@/common/localStorageUtil";
import {
  GetBlockByZipCodeForShopRegisterDocument,
  GetBlockByZipCodeForShopRegisterQuery,
} from "@/generated/graphql";
import {
  createShopRegisterAddressProvider,
  type ShopRegisterAddressProviderContext,
} from "@/components/address-selector/providers/shopRegisterAddressProvider";
import type { KanaStepA, ZipAddressItem, ShopRegisterAddressDeps } from "../types";

/** 店舗登録画面の住所・郵便番号検索を扱う composable。 */
export const useShopRegisterAddress = (deps: ShopRegisterAddressDeps) => {
  const {
    shopCreateInput,
    chainSelectValues,
    ensureChainCode,
    errdialogRefSetter,
    areaDialog,
  } = deps;

  const provider = createShopRegisterAddressProvider();
  const providerContext: ShopRegisterAddressProviderContext = {
    shopCreateInput: shopCreateInput as ShopRegisterAddressProviderContext["shopCreateInput"],
    chainCode: "",
    ensureChainCode,
  };

  const errorZipCode = ref<string>("");
  const errorMessage = ref<string>("");
  const clickedBlockAddress = ref<boolean>(false);
  const blockListFlg = ref<boolean>(false);

  const prefValue = ref<string>("");
  const cityValue = ref<string>("");
  const sectionValue = ref<string>("");

  const zipAddressList = ref<ZipAddressItem[] | null | undefined>();

  const cityBlockList = ref<KanaStepA | null | undefined>(null);
  const sectionBlockList = ref<KanaStepA | null | undefined>(null);
  const blockList = ref<Array<string | null | undefined> | null | undefined>([]);

  /** 現在の chain_code を優先順で解決して provider context に反映する。 */
  const resolveAndSyncChainCode = (): string => {
    const chainCode =
      shopCreateInput.chain_code ||
      chainSelectValues.value?.chain_code ||
      ensureChainCode();

    providerContext.chainCode = chainCode;
    if (!shopCreateInput.chain_code && chainCode) {
      shopCreateInput.chain_code = chainCode;
    }

    return chainCode;
  };

  /** 丁目候補を表示用配列へ変換する。 */
  const toBlockNames = (
    blocks: Array<{ name: string | null | undefined; enabled: boolean }>
  ): Array<string | null | undefined> => {
    return blocks.map((block) => block.name);
  };

  /** 郵便番号入力イベント @input */
  const onZipCodeInput = async (): Promise<void> => {
    shopCreateInput.address1 = "";
    errorZipCode.value = "";
    errorMessage.value = "";
    clickedBlockAddress.value = false;
    blockListFlg.value = false;

    cityBlockList.value = null;
    sectionBlockList.value = null;
    blockList.value = [];
    prefValue.value = "";
    cityValue.value = "";
    sectionValue.value = "";

    if (shopCreateInput.zip_code.length < 7) {
      zipAddressList.value = undefined;
      return;
    }

    try {
      const chainCode = resolveAndSyncChainCode();
      const apolloClient = await generateApolloClient();

      // createShop 時の lot_code 解決に必要な原データを保持する。
      const addressBlockResult: ApolloQueryResult<GetBlockByZipCodeForShopRegisterQuery> =
        await apolloClient.query<GetBlockByZipCodeForShopRegisterQuery>({
          query: GetBlockByZipCodeForShopRegisterDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: chainCode,
            zip_code: shopCreateInput.zip_code,
          },
          fetchPolicy: "no-cache",
        });
      zipAddressList.value = addressBlockResult.data.getBlockByZipCodeForShopRegister;
      if (!zipAddressList.value || zipAddressList.value.length === 0) {
        errorMessage.value = "郵便番号が正しくないか、または配達可能な住所がありません";
        errdialogRefSetter(true, errorMessage.value);
        return;
      }

      const result = await provider.searchByZip(
        { zipCode: shopCreateInput.zip_code },
        providerContext
      );

      prefValue.value = result.selectedPref ?? "";
      cityValue.value = result.selectedCity ?? "";
      sectionValue.value = result.selectedSection ?? "";

      if (result.resolved) {
        await provider.applyResolvedAddress(result.resolved, providerContext);
        areaDialog.value = false;
        clickedBlockAddress.value = true;
        blockList.value = [];
        return;
      }

      const hasCandidates =
        result.blockList.length > 0 ||
        result.cityBlockList !== null ||
        result.sectionBlockList !== null;

      if (!hasCandidates) {
        errorZipCode.value = "入力した郵便番号はご利用対象外です";
        shopCreateInput.address1 = "";
        return;
      }

      cityBlockList.value = result.cityBlockList;
      sectionBlockList.value = result.sectionBlockList;

      if (result.blockList.length > 0) {
        blockList.value = toBlockNames(result.blockList);
        shopCreateInput.address1 = `${prefValue.value}${cityValue.value}${sectionValue.value}`;
        areaDialog.value = false;
        return;
      }

      blockList.value = [];
      areaDialog.value = true;
    } catch (err: unknown) {
      console.error(err);
      errdialogRefSetter(
        true,
        err instanceof Error ? err.message : "不明なエラーが発生しました。"
      );
      await areaClear();
    }
  };

  /** 市区のリンクボタン選択時 @click */
  const onClickCityBlockLink = async (
    item: string | null | undefined
  ): Promise<void> => {
    if (!item) return;

    try {
      resolveAndSyncChainCode();
      blockList.value = [];
      sectionValue.value = "";
      sectionBlockList.value = await provider.getSectionListByPrefCity(
        {
          pref: prefValue.value,
          city: item,
        },
        providerContext
      );
      cityValue.value = item;
    } catch (err: unknown) {
      console.error(err);
      errdialogRefSetter(
        true,
        err instanceof Error ? err.message : "町村の取得に失敗しました。"
      );
      await areaClear();
    }
  };

  /** 町村のリンクボタン選択時 @click */
  const onClickSectionBlockLink = async (
    item: string | null | undefined
  ): Promise<void> => {
    if (!item) return;

    try {
      resolveAndSyncChainCode();

      const resolved = await provider.resolveZipByPrefCitySection(
        {
          pref: prefValue.value,
          city: cityValue.value,
          section: item,
        },
        providerContext
      );

      if (!resolved) {
        throw new Error("該当する郵便番号が見つかりませんでした。");
      }

      await provider.applyResolvedAddress(resolved, providerContext);
      sectionValue.value = item === "その他" ? "" : item;

      if (item === "その他") {
        blockList.value = [];
        blockListFlg.value = false;
        areaDialog.value = false;
        return;
      }

      const nextBlockList = await provider.getBlockListByPrefCitySection(
        {
          pref: prefValue.value,
          city: cityValue.value,
          section: item,
        },
        providerContext
      );

      const blockNames = toBlockNames(nextBlockList);
      const hasNoBlock =
        blockNames.length === 0 ||
        (blockNames.length === 1 && (!blockNames[0] || blockNames[0] === ""));

      if (hasNoBlock) {
        blockList.value = [];
        blockListFlg.value = false;
        areaDialog.value = false;
        return;
      }

      blockList.value = blockNames;
      blockListFlg.value = true;
      areaDialog.value = false;
    } catch (err: unknown) {
      console.error(err);
      errdialogRefSetter(
        true,
        err instanceof Error ? err.message : "不明なエラーが発生しました。"
      );
      await areaClear();
    }
  };

  /** 丁目のリンクボタン選択時 @click */
  const onClickAddressBlockLink = async (
    item: string | null | undefined
  ): Promise<void> => {
    if (item === null || item === undefined) return;

    if (item === "" || item === "その他") {
      // その他が選択された場合、丁目を空にする。
      shopCreateInput.address1 = `${prefValue.value}${cityValue.value}${sectionValue.value}`;
    } else {
      shopCreateInput.address1 = `${prefValue.value}${cityValue.value}${sectionValue.value}${item}`;
    }

    clickedBlockAddress.value = true;
    blockListFlg.value = false;
    blockList.value = [];
  };

  /** エリア選択クリア処理 */
  const areaClear = async (): Promise<void> => {
    sectionBlockList.value = null;
    cityBlockList.value = null;
    blockList.value = [];
    blockListFlg.value = false;
    prefValue.value = "";
    cityValue.value = "";
    sectionValue.value = "";
  };

  return {
    errorZipCode,
    errorMessage,
    clickedBlockAddress,
    blockListFlg,
    prefValue,
    cityValue,
    sectionValue,
    zipAddressList,
    cityBlockList,
    sectionBlockList,
    blockList,
    onZipCodeInput,
    onClickCityBlockLink,
    onClickSectionBlockLink,
    onClickAddressBlockLink,
    areaClear,
  };
};
